/**
 * /timesheet/wall — Read-only crew dashboard designed for office TVs/monitors.
 * Auto-refreshes every 60 seconds. Shows each crew member's weekly total,
 * progress vs 40h goal, and a 7-day strip of daily totals.
 *
 * Auth: any logged-in admin/manager/super_user. Render no nav chrome.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  format, startOfWeek, endOfWeek, addWeeks, subWeeks,
  eachDayOfInterval, getWeek,
} from 'date-fns';
import { ChevronLeft, ChevronRight, RefreshCw, ArrowLeft, Maximize2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKLY_GOAL_HOURS = 40;

// Minutes → "Hh Mm" compact display.
const fmtMin = (m) => {
  if (!m || m <= 0) return '—';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h === 0) return `${mm}m`;
  if (mm === 0) return `${h}h`;
  return `${h}h ${mm}m`;
};

// Status color based on weekly progress against 40h goal.
const getStatusColor = (totalMins) => {
  const hours = totalMins / 60;
  if (hours >= 35) return 'emerald';
  if (hours >= 20) return 'amber';
  if (hours > 0) return 'sky';
  return 'slate';
};

export default function TimesheetWall() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState(new Date());
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const isAuthorized = user?.role === 'admin' || user?.role === 'manager' || user?.role === 'super_user';

  const fetchSummary = useCallback(async () => {
    try {
      const start = format(startOfWeek(selectedWeek, { weekStartsOn: 0 }), 'yyyy-MM-dd');
      const end = format(endOfWeek(selectedWeek, { weekStartsOn: 0 }), 'yyyy-MM-dd');
      const res = await axios.get(`${API}/timesheets/team/summary?start_date=${start}&end_date=${end}`);
      // Filter out users with no calendar color = likely office-only — but keep everyone for now
      // Sort: highest total hours first, then alphabetically
      const sorted = [...res.data].sort((a, b) => {
        const am = a.total_minutes || 0;
        const bm = b.total_minutes || 0;
        if (am !== bm) return bm - am;
        return (a.user_name || '').localeCompare(b.user_name || '');
      });
      setSummary(sorted);
      setLastRefresh(new Date());
    } catch (e) {
      console.error('Failed to load team summary', e);
      toast.error('Failed to refresh');
    } finally {
      setLoading(false);
    }
  }, [selectedWeek]);

  useEffect(() => {
    if (!isAuthorized) return;
    fetchSummary();
    const id = setInterval(fetchSummary, 60000); // refresh every 60s
    return () => clearInterval(id);
  }, [fetchSummary, isAuthorized]);

  const goFullscreen = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
  };

  if (!isAuthorized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white">
        <div className="text-center">
          <p className="text-2xl mb-4">Not authorized</p>
          <Button onClick={() => navigate('/timesheet')}>Back to Timesheet</Button>
        </div>
      </div>
    );
  }

  const weekStart = startOfWeek(selectedWeek, { weekStartsOn: 0 });
  const weekEnd = endOfWeek(selectedWeek, { weekStartsOn: 0 });
  const dayLabels = eachDayOfInterval({ start: weekStart, end: weekEnd }).map(d => format(d, 'EEEEE'));

  // Crew totals across everyone
  const totalCrewMins = summary.reduce((s, m) => s + (m.total_minutes || 0), 0);
  const activeCrew = summary.filter(m => (m.total_minutes || 0) > 0).length;

  return (
    <div className="min-h-screen bg-slate-900 text-white" data-testid="timesheet-wall">
      {/* Header bar */}
      <div className="border-b border-slate-700 px-6 py-3 flex items-center justify-between bg-slate-950">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => navigate('/timesheet')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> Exit
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Crew Time — Week {getWeek(selectedWeek)}</h1>
            <p className="text-sm text-slate-400">{format(weekStart, 'MMM d')} – {format(weekEnd, 'MMM d, yyyy')}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => setSelectedWeek(subWeeks(selectedWeek, 1))} data-testid="wall-prev-week">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => setSelectedWeek(new Date())}>
            This Week
          </Button>
          <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={() => setSelectedWeek(addWeeks(selectedWeek, 1))} data-testid="wall-next-week">
            <ChevronRight className="w-5 h-5" />
          </Button>
          <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800 ml-2" onClick={fetchSummary} title="Refresh now" data-testid="wall-refresh">
            <RefreshCw className="w-4 h-4" />
          </Button>
          <Button variant="ghost" size="sm" className="text-slate-300 hover:text-white hover:bg-slate-800" onClick={goFullscreen} title="Fullscreen" data-testid="wall-fullscreen">
            <Maximize2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Crew stats strip */}
      <div className="px-6 py-4 border-b border-slate-800 bg-slate-950 flex items-center gap-8 text-sm">
        <div>
          <span className="text-slate-400 mr-2">Active Crew:</span>
          <span className="text-white font-semibold text-lg" data-testid="wall-active-crew">{activeCrew}</span>
          <span className="text-slate-500"> / {summary.length}</span>
        </div>
        <div>
          <span className="text-slate-400 mr-2">Total Crew Hours:</span>
          <span className="text-amber-400 font-semibold text-lg" data-testid="wall-total-hours">{fmtMin(totalCrewMins)}</span>
        </div>
        <div className="ml-auto text-xs text-slate-500">
          Auto-refresh every 60s · last: {format(lastRefresh, 'h:mm:ss a')}
        </div>
      </div>

      {/* Grid of crew cards */}
      <div className="p-6">
        {loading ? (
          <div className="text-center py-20 text-slate-500">Loading…</div>
        ) : summary.length === 0 ? (
          <div className="text-center py-20 text-slate-500">No team members found</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {summary.map((m) => {
              const totalMins = m.total_minutes || 0;
              const hours = totalMins / 60;
              const pct = Math.min(100, Math.round((hours / WEEKLY_GOAL_HOURS) * 100));
              const color = getStatusColor(totalMins);
              const colorMap = {
                emerald: { ring: 'border-emerald-500', bar: 'bg-emerald-500', text: 'text-emerald-300', pillBg: 'bg-emerald-900/40 text-emerald-300' },
                amber:   { ring: 'border-amber-500',   bar: 'bg-amber-500',   text: 'text-amber-300',   pillBg: 'bg-amber-900/40 text-amber-300' },
                sky:     { ring: 'border-sky-500',     bar: 'bg-sky-500',     text: 'text-sky-300',     pillBg: 'bg-sky-900/40 text-sky-300' },
                slate:   { ring: 'border-slate-700',   bar: 'bg-slate-600',   text: 'text-slate-400',   pillBg: 'bg-slate-800 text-slate-500' },
              }[color];
              const daily = m.daily_minutes || [0, 0, 0, 0, 0, 0, 0];
              const dailyMax = Math.max(...daily, 60);

              return (
                <div
                  key={m.user_id}
                  className={`bg-slate-800 border ${colorMap.ring} rounded-lg p-4 transition-all`}
                  data-testid={`wall-card-${m.user_id}`}
                >
                  {/* Name + initials */}
                  <div className="flex items-center gap-3 mb-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-base"
                      style={{ backgroundColor: m.calendar_color || '#475569', color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.4)' }}
                    >
                      {(m.user_name || '?').split(' ').map(s => s[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-white truncate">{m.user_name}</p>
                      <p className={`text-xs ${colorMap.text}`}>
                        {m.entry_count} {m.entry_count === 1 ? 'entry' : 'entries'}
                      </p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-semibold ${colorMap.pillBg}`}>{pct}%</span>
                  </div>

                  {/* Big total hours */}
                  <div className="flex items-baseline gap-2 mb-2">
                    <span className="text-3xl font-bold text-white">{hours.toFixed(1)}h</span>
                    <span className="text-sm text-slate-400">/ {WEEKLY_GOAL_HOURS}h</span>
                  </div>

                  {/* Progress bar */}
                  <div className="w-full h-2 bg-slate-700 rounded-full overflow-hidden mb-3">
                    <div
                      className={`h-full ${colorMap.bar} transition-all`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>

                  {/* Daily strip — 7 mini-bars Sun..Sat */}
                  <div className="flex items-end gap-1 h-8 mb-1">
                    {daily.map((mins, i) => {
                      const h = mins > 0 ? Math.max(4, (mins / dailyMax) * 32) : 2;
                      return (
                        <div
                          key={i}
                          className={`flex-1 rounded-t ${mins > 0 ? colorMap.bar : 'bg-slate-700'}`}
                          style={{ height: `${h}px`, opacity: mins > 0 ? 0.85 : 0.4 }}
                          title={`${dayLabels[i]}: ${fmtMin(mins)}`}
                        />
                      );
                    })}
                  </div>
                  <div className="flex justify-between text-[10px] text-slate-500 font-mono uppercase">
                    {dayLabels.map((d, i) => <span key={i} className="flex-1 text-center">{d}</span>)}
                  </div>

                  {/* On-job vs travel split */}
                  <div className="mt-3 pt-3 border-t border-slate-700 flex justify-between text-xs items-end">
                    <div>
                      <p className="text-slate-500">On Job</p>
                      <p className="text-slate-300 font-semibold">{fmtMin(m.on_job_minutes || 0)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Travel</p>
                      <p className="text-slate-300 font-semibold">{fmtMin(m.travel_minutes || 0)}</p>
                    </div>
                    {/* Quick link to that employee's week on the Team
                        Timesheets tab. Mirrors the Approval Dashboard
                        "View" eye icon so behavior is consistent. */}
                    <button
                      type="button"
                      onClick={() => navigate(`/timesheet?user=${m.user_id}&week=${format(weekStart, 'yyyy-MM-dd')}`)}
                      className="text-amber-400 hover:text-amber-300 text-xs font-semibold underline-offset-2 hover:underline"
                      data-testid={`wall-view-week-${m.user_id}`}
                    >
                      View week →
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
